import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type PointerEvent,
} from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../auth/AuthContext";
import { refreshCurrentPageData } from "../hooks/dataRefresh";
import { useI18n, type MsgKey } from "../i18n";
import {
  NAV_TARGET_ATTRIBUTE,
  OPERATIONS_NAV,
  REVIEW_NAV,
  STANDARDS_EDIT_PERM,
  STANDARDS_NAV,
  SUPER_ADMIN,
  SUPER_ADMIN_NAV,
  type NavGroup,
  type NavItem,
} from "./navigation";
import {
  OPEN_REVIEW_PROGRESS_EVENT,
  REVIEW_PROGRESS_HAS_ITEMS_EVENT,
  ReviewProgressPanel,
} from "./ReviewProgressPanel";
import { ToastHost, showToast } from "./Toast";
import { SystemGuide } from "./systemGuide/SystemGuide";
import { ReportProblemDialog } from "../features/support/ReportProblemDialog";
import { REVEAL_NAV_TARGET_EVENT, type RevealNavTargetEvent } from "./systemGuide/guideData";
import { clearLessonDraftJob } from "../features/educateSage/lessonDraftJob";

const navItemTone = (isActive: boolean) =>
  isActive ? "bg-brand-50 text-brand-700" : "text-slate-600 hover:bg-slate-100";

const navLinkClass = ({ isActive }: { isActive: boolean }) =>
  `block rounded-md px-3 py-1.5 text-base font-medium ${navItemTone(isActive)}`;

const topNavLinkClass = ({ isActive }: { isActive: boolean }) =>
  `whitespace-nowrap rounded-md px-3 py-1.5 text-base font-medium ${navItemTone(isActive)}`;

function isWithin(pathname: string, to: string) {
  return pathname === to || pathname.startsWith(`${to}/`);
}

type TopNavEntry =
  | { kind: "link"; item: NavItem }
  | { kind: "menu"; id: string; labelKey: MsgKey; groups: NavGroup[] };

const SIDEBAR_DEFAULT_WIDTH = 208;
const SIDEBAR_MIN_WIDTH = 176;
const SIDEBAR_MAX_WIDTH = 384;

function clampSidebarWidth(width: number) {
  return Math.min(SIDEBAR_MAX_WIDTH, Math.max(SIDEBAR_MIN_WIDTH, width));
}

function getStoredSidebarWidth() {
  const stored = Number(localStorage.getItem("cs-sidebar-width"));
  return Number.isFinite(stored) && stored >= SIDEBAR_MIN_WIDTH && stored <= SIDEBAR_MAX_WIDTH
    ? stored
    : SIDEBAR_DEFAULT_WIDTH;
}

/**
 * The review the reporter is looking at, if any. The footer report button is
 * the catch-all path, but a client who clicks it while a review is on screen
 * means that review — reading it off the route is what stops the ticket from
 * arriving with nothing but the reporter's sentence in it.
 */
function reviewIdFromPath(pathname: string): string | undefined {
  const match = /^\/reviews\/([^/]+)/.exec(pathname);
  const raw = match?.[1];
  if (!raw) return undefined;
  const id = decodeURIComponent(raw);
  return id === "new" ? undefined : id;
}

export function Layout() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const {
    user,
    platformRole,
    activeOrganization,
    availableOrganizations,
    switchOrganization,
    switchingOrganization,
    logout,
    can,
  } = useAuth();
  const { t, lang, setLang } = useI18n();
  const visible = (items: NavItem[]) => items.filter((item) => !item.perm || can(item.perm));
  const standardsReadOnly = !can(STANDARDS_EDIT_PERM);
  const standardsItems = visible(STANDARDS_NAV).filter(
    (item) => !standardsReadOnly || ["/policies", "/rules", "/dictionary"].includes(item.to),
  );
  const reviewItems = visible(REVIEW_NAV);
  const operationsItems = visible(OPERATIONS_NAV);
  // Requesters get direct daily-work links and a separate reference library.
  // Administrators retain the full standards and operations menus.
  const topNav: TopNavEntry[] = [];
  if (standardsItems.length > 0 && !standardsReadOnly) {
    topNav.push({
      kind: "menu",
      id: "standards",
      labelKey: "sidebarSectionStandards",
      groups: [{ items: standardsItems }],
    });
  }
  for (const item of reviewItems) {
    if (standardsReadOnly && item.to === "/support" && standardsItems.length > 0) {
      topNav.push({
        kind: "menu",
        id: "references",
        labelKey: "sidebarSectionStandardsReadOnly",
        groups: [{ items: standardsItems }],
      });
    }
    const labelKey = standardsReadOnly && item.to === "/"
      ? "navHome"
      : standardsReadOnly && item.to === "/reviews"
        ? "navMyReviews"
        : item.key;
    topNav.push({ kind: "link", item: { ...item, key: labelKey } });
  }
  if (operationsItems.length > 0) {
    topNav.push({
      kind: "menu",
      id: "operations",
      labelKey: "sidebarSectionOperations",
      groups: [{ items: operationsItems }],
    });
  }
  const isSuperAdminArea = isWithin(pathname, SUPER_ADMIN.home);
  const showTenantNav = !isSuperAdminArea && Boolean(activeOrganization);
  // Every tenant menu is in the top bar; only the platform area keeps a sidebar.
  const hasSidebar = isSuperAdminArea && platformRole === "SUPER_ADMIN";
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(
    () => localStorage.getItem("cs-sidebar-collapsed") === "true",
  );
  const [isSidebarHoverOpen, setIsSidebarHoverOpen] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState(getStoredSidebarWidth);
  const [isSidebarResizing, setIsSidebarResizing] = useState(false);
  const [isSystemGuideOpen, setIsSystemGuideOpen] = useState(false);
  // Always-available fallback for a problem with no review to anchor to — a
  // page that will not load is exactly the case a client cannot describe well.
  const [isReportOpen, setIsReportOpen] = useState(false);
  const [hasReviewItems, setHasReviewItems] = useState(false);
  const [activeReviewCount, setActiveReviewCount] = useState(0);
  const refreshInFlight = useRef(false);
  const organizationMenu = useRef<HTMLDetailsElement>(null);
  const topNavigation = useRef<HTMLElement | null>(null);
  const appShell = useRef<HTMLDivElement>(null);
  const headerRow = useRef<HTMLDivElement>(null);
  const headerLeading = useRef<HTMLDivElement>(null);
  const headerCenter = useRef<HTMLElement | null>(null);
  const headerTrailing = useRef<HTMLDivElement>(null);
  const sidebarResizeStart = useRef<{ x: number; width: number } | null>(null);
  // Equal side columns stay centered only while each is at least as wide as the
  // wider of the logo and the controls. Below that, text wraps instead of holding
  // the row, so the bar remembers that width and the page scrolls.
  const [headerSideWidth, setHeaderSideWidth] = useState(0);
  const queryClient = useQueryClient();

  useEffect(() => {
    localStorage.setItem("cs-sidebar-collapsed", String(isSidebarCollapsed));
  }, [isSidebarCollapsed]);

  useEffect(() => {
    localStorage.setItem("cs-sidebar-width", String(sidebarWidth));
  }, [sidebarWidth]);

  useLayoutEffect(() => {
    const row = headerRow.current;
    const leading = headerLeading.current;
    const trailing = headerTrailing.current;
    if (!row || !leading || !trailing) return;

    const measure = () => {
      const side = Math.ceil(Math.max(leading.offsetWidth, trailing.offsetWidth));
      const styles = getComputedStyle(row);
      const padding = parseFloat(styles.paddingLeft) + parseFloat(styles.paddingRight);
      const gap = parseFloat(styles.columnGap) || 0;
      const center = headerCenter.current?.offsetWidth ?? 0;
      const needed = Math.ceil(side * 2 + center + gap * 2 + padding);
      if (appShell.current) appShell.current.style.minWidth = `${needed}px`;
      setHeaderSideWidth((current) => (current === side ? current : side));
    };

    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(leading);
    observer.observe(trailing);
    if (headerCenter.current) observer.observe(headerCenter.current);
    return () => observer.disconnect();
  }, [lang, showTenantNav, topNav.length, hasReviewItems, platformRole, activeOrganization?.id, user?.fullName]);

  useEffect(() => {
    const reveal = (event: Event) => {
      const { to } = (event as RevealNavTargetEvent).detail;
      setIsSidebarHoverOpen(false);
      setIsSidebarCollapsed(false);
      // Open only the dropdown holding the target, so the tour can point at it.
      topNavigation.current?.querySelectorAll("details").forEach((menu) => {
        menu.open = to !== null && menu.querySelector(`[${NAV_TARGET_ATTRIBUTE}="${to}"]`) !== null;
      });
    };
    window.addEventListener(REVEAL_NAV_TARGET_EVENT, reveal);
    return () => window.removeEventListener(REVEAL_NAV_TARGET_EVENT, reveal);
  }, []);

  useEffect(() => {
    // Also what keeps a single dropdown open: opening one is a click outside the others.
    const closeOnOutsidePointer = (event: globalThis.PointerEvent) => {
      topNavigation.current?.querySelectorAll("details").forEach((menu) => {
        if (menu.open && !menu.contains(event.target as Node)) menu.open = false;
      });
    };
    const closeOnEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key === "Escape") closeTopMenus();
    };
    document.addEventListener("pointerdown", closeOnOutsidePointer);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOnOutsidePointer);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, []);

  function closeTopMenus() {
    topNavigation.current?.querySelectorAll("details").forEach((menu) => {
      menu.open = false;
    });
  }

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = e as Event & { hasItems: boolean; activeCount: number };
      setHasReviewItems(detail.hasItems);
      setActiveReviewCount(detail.activeCount ?? 0);
    };
    window.addEventListener(REVIEW_PROGRESS_HAS_ITEMS_EVENT, handler);
    return () => window.removeEventListener(REVIEW_PROGRESS_HAS_ITEMS_EVENT, handler);
  }, []);

  const previousOrganizationId = useRef(activeOrganization?.id);
  useEffect(() => {
    if (previousOrganizationId.current && previousOrganizationId.current !== activeOrganization?.id) {
      clearLessonDraftJob();
      // Tenant lists (support, reviews, policies) are cached by query key; drop
      // them so selecting NURIE cannot keep showing the previous org's empty page.
      void queryClient.invalidateQueries();
    }
    previousOrganizationId.current = activeOrganization?.id;
  }, [activeOrganization?.id, queryClient]);

  /**
   * Switching lands on the home page rather than staying put: the current route
   * is often a record of the organization being left behind, and the new
   * organization has no such record to show under that same URL.
   */
  const changeOrganization = async (organizationId: string) => {
    if (organizationMenu.current) organizationMenu.current.open = false;
    if (organizationId === activeOrganization?.id) return;
    try {
      await switchOrganization(organizationId);
      navigate("/");
    } catch {
      showToast({ tone: "error", message: t("switchOrganizationFailed") });
    }
  };

  useEffect(() => {
    if (!user?.userId) return;

    const runRefresh = async () => {
      if (refreshInFlight.current) return;
      refreshInFlight.current = true;
      try {
        // Pages still on useAsync listen for this event; React Query-backed
        // pages are refreshed directly so both mechanisms stay in sync.
        await Promise.all([refreshCurrentPageData(), queryClient.invalidateQueries()]);
      } finally {
        refreshInFlight.current = false;
      }
    };

    const handleVisibility = () => {
      if (document.visibilityState === "visible") void runRefresh();
    };

    const timer = window.setInterval(() => void runRefresh(), 5 * 60 * 1000);
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [user?.userId]);

  const handleSidebarResizeStart = (event: PointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    sidebarResizeStart.current = { x: event.clientX, width: sidebarWidth };
    event.currentTarget.setPointerCapture(event.pointerId);
    setIsSidebarResizing(true);
  };

  const handleSidebarResize = (event: PointerEvent<HTMLDivElement>) => {
    if (!sidebarResizeStart.current) return;
    setSidebarWidth(
      clampSidebarWidth(
        sidebarResizeStart.current.width + event.clientX - sidebarResizeStart.current.x,
      ),
    );
  };

  const handleSidebarResizeEnd = (event: PointerEvent<HTMLDivElement>) => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    sidebarResizeStart.current = null;
    setIsSidebarResizing(false);
  };

  const handleSidebarResizeKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const step = 16;
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      setSidebarWidth((width) => clampSidebarWidth(width - step));
    } else if (event.key === "ArrowRight") {
      event.preventDefault();
      setSidebarWidth((width) => clampSidebarWidth(width + step));
    } else if (event.key === "Home") {
      event.preventDefault();
      setSidebarWidth(SIDEBAR_MIN_WIDTH);
    } else if (event.key === "End") {
      event.preventDefault();
      setSidebarWidth(SIDEBAR_MAX_WIDTH);
    }
  };

  const isSidebarDrawerOpen = !isSidebarCollapsed || isSidebarHoverOpen;

  const headerColumns =
    headerSideWidth > 0
      ? `minmax(${headerSideWidth}px, 1fr) auto minmax(${headerSideWidth}px, 1fr)`
      : "max-content auto max-content";

  return (
    <div ref={appShell} className="flex min-h-screen flex-col">
      <header className="sticky top-0 z-50 border-b border-slate-200 bg-white">
        {/* Equal outer columns keep the menu centered on the page, not between the
            logo and the controls, which differ in width. The shared minimum is the
            wider side, so a narrow window scrolls instead of wrapping the row. */}
        <div
          ref={headerRow}
          className="grid w-full items-center gap-4 px-4 py-2.5 sm:px-5"
          style={{ gridTemplateColumns: headerColumns }}
        >
          <div ref={headerLeading} className="w-max justify-self-start">
            <Link
              to="/"
              className="inline-flex items-center gap-3 whitespace-nowrap transition-opacity hover:opacity-80"
            >
            <img
              src="/ComplianceSage.png"
              alt={`${t("appName")} logo`}
              className="h-10 w-10 shrink-0"
            />
            <div>
              <div className="text-lg font-bold text-brand-700">{t("appName")}</div>
              <div className="text-xs text-slate-500">{t("tagline")}</div>
            </div>
            </Link>
          </div>
          {showTenantNav && topNav.length > 0 ? (
            <nav
              ref={(node) => {
                topNavigation.current = node;
                headerCenter.current = node;
              }}
              className="flex shrink-0 flex-nowrap items-center justify-center gap-1"
              aria-label={t("topNavigation")}
            >
              {topNav.map((entry) =>
                entry.kind === "menu" ? (
                  <details key={entry.id} className="relative">
                    <summary
                      className={`flex cursor-pointer list-none items-center gap-1 whitespace-nowrap rounded-md px-3 py-1.5 text-base font-medium ${navItemTone(
                        entry.groups.some((group) =>
                          group.items.some((item) => isWithin(pathname, item.to)),
                        ),
                      )}`}
                    >
                      {t(entry.labelKey)}
                      <span aria-hidden="true" className="text-xs text-slate-400">▾</span>
                    </summary>
                    <div className="absolute left-1/2 z-20 mt-2 min-w-56 -translate-x-1/2 space-y-1 rounded-md border border-slate-200 bg-white p-2 shadow-lg">
                      {entry.groups.map((group, idx) => (
                        <div
                          key={group.headingKey ?? idx}
                          className={idx > 0 ? "mt-2 space-y-1 border-t border-slate-200 pt-2" : "space-y-1"}
                        >
                          {group.headingKey ? (
                            <p className="px-3 pb-1 pt-1 text-sm font-bold text-brand-700">
                              {t(group.headingKey)}
                            </p>
                          ) : null}
                          {group.items.map((item) => (
                            <NavLink
                              key={item.to}
                              to={item.to}
                              className={navLinkClass}
                              onClick={closeTopMenus}
                              {...{ [NAV_TARGET_ATTRIBUTE]: item.to }}
                            >
                              {t(item.key)}
                            </NavLink>
                          ))}
                        </div>
                      ))}
                    </div>
                  </details>
                ) : (
                  <NavLink
                    key={entry.item.to}
                    to={entry.item.to}
                    end={entry.item.to === "/"}
                    className={topNavLinkClass}
                    {...{ [NAV_TARGET_ATTRIBUTE]: entry.item.to }}
                  >
                    {t(entry.item.key)}
                  </NavLink>
                ),
              )}
            </nav>
          ) : (
            <div ref={(node) => { headerCenter.current = node; }} />
          )}
          <div ref={headerTrailing} className="flex w-max shrink-0 flex-nowrap items-center gap-3 justify-self-end">
            <button
              type="button"
              className="flex h-9 w-9 items-center justify-center rounded-full border border-slate-300 text-sm font-bold text-slate-600 transition hover:border-brand-500 hover:bg-brand-50 hover:text-brand-700"
              aria-label={t("sysGuideOpen")}
              title={t("sysGuideOpen")}
              onClick={() => setIsSystemGuideOpen(true)}
            >
              <span aria-hidden="true">?</span>
            </button>
            {activeOrganization && hasReviewItems ? (
              <button
                type="button"
                className="relative flex h-9 w-9 items-center justify-center rounded-md text-lg text-slate-600 transition hover:bg-slate-100"
                aria-label={
                  activeReviewCount > 0
                    ? t("analysisProgressActiveBadge", { count: activeReviewCount })
                    : t("analysisProgressRestore")
                }
                title={
                  activeReviewCount > 0
                    ? t("analysisProgressActiveBadge", { count: activeReviewCount })
                    : t("analysisProgressRestore")
                }
                onClick={() => window.dispatchEvent(new Event(OPEN_REVIEW_PROGRESS_EVENT))}
              >
                <span aria-hidden="true">⚠️</span>
                {activeReviewCount > 0 && (
                  <span
                    aria-hidden="true"
                    className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-brand-600 px-1 text-[10px] font-bold leading-none text-white"
                  >
                    {activeReviewCount}
                  </span>
                )}
              </button>
            ) : null}
            {platformRole === "SUPER_ADMIN" ? (
              <NavLink
                to={SUPER_ADMIN.home}
                aria-label={t("navSuperAdmin")}
                title={t("navSuperAdmin")}
                className={({ isActive }) =>
                  `flex h-9 w-9 items-center justify-center rounded-md text-lg transition ${
                    isActive || isSuperAdminArea
                      ? "bg-brand-50 text-brand-700"
                      : "text-slate-600 hover:bg-slate-100"
                  }`
                }
              >
                <span aria-hidden="true">🔒</span>
              </NavLink>
            ) : null}
            {!isSuperAdminArea && activeOrganization ? (
              // One organization is not a choice, so it stays a plain link home;
              // the picker appears only for people who belong to several.
              availableOrganizations.length > 1 ? (
                <details className="relative" ref={organizationMenu}>
                  <summary
                    className="flex cursor-pointer list-none items-center gap-1 rounded-md px-2 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-100 hover:text-brand-700"
                    title={t("switchOrganization")}
                    aria-label={t("switchOrganization")}
                  >
                    <span className="max-w-44 truncate">{activeOrganization.name}</span>
                    <span aria-hidden="true" className="text-xs text-slate-400">▾</span>
                  </summary>
                  <div className="absolute right-0 z-20 mt-2 min-w-56 rounded-md border border-slate-200 bg-white p-2 shadow-lg">
                    <div className="px-2 py-1 text-xs text-slate-500">
                      {t("switchOrganization")}
                    </div>
                    {availableOrganizations.map((organization) => {
                      const isCurrent = organization.id === activeOrganization.id;
                      return (
                        <button
                          key={organization.id}
                          type="button"
                          disabled={switchingOrganization}
                          aria-current={isCurrent ? "true" : undefined}
                          className={`mt-1 flex w-full items-center justify-between gap-2 rounded px-2 py-2 text-left text-sm disabled:opacity-60 ${
                            isCurrent
                              ? "bg-brand-50 font-medium text-brand-700"
                              : "text-slate-700 hover:bg-slate-100"
                          }`}
                          onClick={() => void changeOrganization(organization.id)}
                        >
                          <span className="truncate">{organization.name}</span>
                          {isCurrent ? <span aria-hidden="true">✓</span> : null}
                        </button>
                      );
                    })}
                  </div>
                </details>
              ) : (
                <NavLink
                  to="/"
                  className="max-w-44 truncate rounded-md px-2 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-100 hover:text-brand-700"
                  title={activeOrganization.name}
                >
                  {activeOrganization.name}
                </NavLink>
              )
            ) : null}
            <select
              aria-label={t("language")}
              title={t("language")}
              className="rounded-md border border-slate-300 px-2 py-1 text-sm"
              value={lang}
              onChange={(e) => setLang(e.target.value as "ko" | "en")}
            >
              <option value="ko">🇰🇷</option>
              <option value="en">🇺🇸</option>
            </select>
            {user ? (
              <details className="relative">
                <summary className="flex cursor-pointer list-none items-center gap-1 rounded-md px-2 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-100">
                  <span className="max-w-32 truncate">{user.fullName ?? user.email}</span>
                  <span aria-hidden="true" className="text-xs text-slate-400">▾</span>
                </summary>
                <div className="absolute right-0 z-20 mt-2 min-w-48 rounded-md border border-slate-200 bg-white p-2 shadow-lg">
                  <div className="truncate px-2 py-1 text-xs text-slate-500">{user.email}</div>
                  <NavLink
                    to="/profile"
                    className="mt-1 block rounded px-2 py-2 text-sm text-slate-700 hover:bg-slate-100"
                  >
                    {t("navProfile")}
                  </NavLink>
                  {platformRole === "SUPER_ADMIN" ? (
                    <NavLink
                      to={SUPER_ADMIN.home}
                      className="mt-1 block rounded px-2 py-2 text-sm text-slate-700 hover:bg-slate-100"
                    >
                      {t("navSuperAdmin")}
                    </NavLink>
                  ) : null}
                  <button
                    type="button"
                    className="mt-1 w-full rounded px-2 py-2 text-left text-sm text-slate-700 hover:bg-slate-100"
                    onClick={() => void logout()}
                  >
                    {t("logout")}
                  </button>
                </div>
              </details>
            ) : null}
          </div>
        </div>
      </header>

      <div className="flex w-full flex-1 px-4 py-4 sm:px-5">
        {hasSidebar ? (
          <nav
            className={`sticky top-20 z-40 self-start relative shrink-0 transition-[width] duration-200 ${
              isSidebarCollapsed ? "w-9" : ""
            }`}
            style={isSidebarCollapsed ? undefined : { width: sidebarWidth }}
            aria-label={t("sidebarNavigation")}
            onMouseEnter={() => {
              if (isSidebarCollapsed) setIsSidebarHoverOpen(true);
            }}
            onMouseLeave={() => setIsSidebarHoverOpen(false)}
          >
            {isSidebarDrawerOpen ? (
              <div
                className={`max-h-[calc(100vh-6rem)] space-y-6 overflow-y-auto pr-8 ${
                  isSidebarCollapsed
                    ? "absolute left-0 top-0 z-30 rounded-r-lg bg-white shadow-xl"
                    : ""
                }`}
                style={isSidebarCollapsed ? { width: sidebarWidth } : undefined}
              >
                <button
                  type="button"
                  className="absolute right-0 top-0 flex h-8 w-8 items-center justify-center rounded-md text-lg text-slate-600 transition hover:bg-slate-100"
                  aria-label={isSidebarCollapsed ? t("expandSidebar") : t("collapseSidebar")}
                  title={isSidebarCollapsed ? t("expandSidebar") : t("collapseSidebar")}
                  onClick={() => {
                    setIsSidebarHoverOpen(false);
                    setIsSidebarCollapsed((collapsed) => !collapsed);
                  }}
                >
                  <span aria-hidden="true">{isSidebarCollapsed ? "›" : "‹"}</span>
                </button>
                {isSuperAdminArea && platformRole === "SUPER_ADMIN" ? (
                  <section aria-labelledby="super-admin-navigation-heading">
                    <h2
                      id="super-admin-navigation-heading"
                      className="mb-2 px-3 text-xs font-semibold uppercase tracking-wide text-slate-400"
                    >
                      {t("navSuperAdmin")}
                    </h2>
                    <div className="space-y-1">
                      {SUPER_ADMIN_NAV.map((item) => (
                        <NavLink
                          key={item.to}
                          to={item.to}
                          end={item.to === SUPER_ADMIN.home}
                          className={navLinkClass}
                          {...{ [NAV_TARGET_ATTRIBUTE]: item.to }}
                        >
                          {t(item.key)}
                        </NavLink>
                      ))}
                    </div>
                  </section>
                ) : null}
              </div>
            ) : null}
            {isSidebarCollapsed && !isSidebarHoverOpen ? (
              <button
                type="button"
                className="flex h-8 w-8 items-center justify-center rounded-md text-lg text-slate-600 transition hover:bg-slate-100"
                aria-label={t("expandSidebar")}
                title={t("expandSidebar")}
                onClick={() => setIsSidebarCollapsed(false)}
              >
                <span aria-hidden="true">›</span>
              </button>
            ) : null}
          </nav>
        ) : null}
        {hasSidebar && !isSidebarCollapsed ? (
          <div
            className={`mx-1 w-1 shrink-0 touch-none rounded-full transition-colors ${
              isSidebarResizing ? "bg-brand-500" : "bg-slate-200 hover:bg-brand-400"
            } cursor-col-resize`}
            role="separator"
            aria-label={t("resizeSidebar")}
            aria-orientation="vertical"
            aria-valuemin={SIDEBAR_MIN_WIDTH}
            aria-valuemax={SIDEBAR_MAX_WIDTH}
            aria-valuenow={sidebarWidth}
            tabIndex={0}
            onPointerDown={handleSidebarResizeStart}
            onPointerMove={handleSidebarResize}
            onPointerUp={handleSidebarResizeEnd}
            onPointerCancel={handleSidebarResizeEnd}
            onKeyDown={handleSidebarResizeKeyDown}
          />
        ) : null}
        <main className={`min-w-0 flex-1 ${hasSidebar ? "ml-3" : ""}`}>
          <Outlet key={activeOrganization?.id ?? "no-organization"} />
        </main>
      </div>
      <footer className="mt-auto border-t border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-4 py-3 sm:px-5">
          <div className="flex items-center justify-center gap-1.5 text-xs text-slate-400">
            <span>Powered by</span>
            <a
              href="https://vaultsage.ai"
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold text-slate-600 transition-colors hover:text-slate-900"
            >
              VaultSage
            </a>
            <span className="text-slate-300">·</span>
            <span>Built by</span>
            <a
              href="https://nurie.ai"
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold text-slate-600 transition-colors hover:text-slate-900"
            >
              NURIE
            </a>
            {/* The catch-all for problems with no AI result to anchor to. The
                anchored affordances next to each result are the primary path,
                so this one lives in the footer rather than the top bar. */}
            {can("SUPPORT_CREATE") && (
              <>
                <span className="text-slate-300">·</span>
                <button
                  type="button"
                  className="font-semibold text-slate-600 transition-colors hover:text-slate-900"
                  onClick={() => setIsReportOpen(true)}
                >
                  {t("reportProblem")}
                </button>
              </>
            )}
            <img
              src="/SOC2.png"
              alt="AICPA SOC 2 Certified"
              className="ml-1 h-7 w-7 object-contain opacity-80"
            />
          </div>
        </div>
      </footer>
      {activeOrganization ? <ReviewProgressPanel /> : null}
      <ToastHost />
      <SystemGuide open={isSystemGuideOpen} onOpenChange={setIsSystemGuideOpen} />
      <ReportProblemDialog
        open={isReportOpen}
        onClose={() => setIsReportOpen(false)}
        reviewId={reviewIdFromPath(pathname)}
      />
    </div>
  );
}
